import React, { memo, useMemo } from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import CheckIcon from 'icons/check.svg';
import HomeIcon from 'icons/ic-home.svg';
import MinusIcon from 'icons/minus.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import Checkbox from 'components/application/Checkbox';
import CircleQuestion from 'components/application/CircleQuestion';
import ConditionalWrapper from 'components/application/ConditionalWrapper';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import AllowanceBar from 'components/settings/users/AllowanceBar';
import { useUserFormContext } from 'components/settings/users/userForm/contexts/UserFormContext';

import {
  isAnyPermissionActivelySelected,
  isPermissionActivelySelected,
  isPermissionSelected,
  projectPermissions,
} from 'components/helpers/users';

const tooltipDelay = 500;

const isFirstColumnInGroup = (column) =>
  !!column.parent && column.parent.getLeafColumns()[0]?.id === column.id;

const isAllowanceExhaustedForColumn = ({
  columnId,
  doesDomainUserHavePersonnelReporteeManager = false,
  isFullUserAllowanceConsumptionDisabled,
  isPersonnelReporteeManagerAllowanceConsumptionDisabled,
}) =>
  columnId === 'personnelReporteeManager' ?
    isPersonnelReporteeManagerAllowanceConsumptionDisabled &&
    !doesDomainUserHavePersonnelReporteeManager
  : isFullUserAllowanceConsumptionDisabled && columnId !== 'projectViewer';

export default function AccessControlTable({
  assignablePermissions,
  currentUser,
  fullUserAllowanceConsumptionCount,
  fullUserAllowanceCount,
  isAtFullUserCapacity,
  isAtPersonnelReporteeManagerCapacity,
  isFullUserAllowanceConsumptionDisabled,
  isPersonnelReporteeManagerAllowanceConsumptionDisabled,
  onPermissionChange,
  onSelectAllChange,
  personnelReporteeManagerAllowanceConsumptionCount,
  personnelReporteeManagerAllowanceCount,
}) {
  const {
    division: {
      attributes: { pluralRAMSName, singularRAMSName },
    },
  } = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.userForm.components.AccessControlTable',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { divisions, domainUser, personnelText } = useUserFormContext();

  const doesDomainUserHavePersonnelReporteeManagerForDivision = (
    divisionId,
  ) => {
    const accessControl = domainUser?.included.find(
      ({ relationships, type }) =>
        type === 'accessControl' &&
        relationships.company.data.id === divisionId,
    );

    return !!accessControl?.attributes.personnelReporteeManager;
  };

  const isCourseApplicableResourceForAtLeastOneDivisionWithinAccount =
    divisions.some((division) => division.meta.isCourseApplicableResource);

  const areProjectsEnabled = assignablePermissions.some((permission) =>
    projectPermissions.includes(permission),
  );

  const isPersonnelReporteeManagerEnabled = assignablePermissions.includes(
    'personnelReporteeManager',
  );

  const isAtAnyAllowanceCapacity =
    isAtFullUserCapacity ||
    (isPersonnelReporteeManagerEnabled && isAtPersonnelReporteeManagerCapacity);

  const columnTranslations = useMemo(
    () => ({
      division: i18nTCommon('shared.division'),
      permissions: {
        labels: {
          administrator: i18nTCommon('settings.userRoles.divisionAdmin.label'),
          personnelEditor: i18nTCommon(
            'settings.userRoles.personnelEditor.label',
          ),
          personnelReporteeManager: i18nTCommon(
            'settings.userRoles.personnelReporteeManager.label',
          ),
          personnelViewer: i18nTCommon(
            'settings.userRoles.personnelViewer.label',
          ),
          rolesAndCoursesEditor:
            isCourseApplicableResourceForAtLeastOneDivisionWithinAccount ?
              i18nTCommon('settings.userRoles.rolesAndCoursesEditor.label')
            : i18nTCommon('settings.userRoles.rolesEditor.label'),
          ...(areProjectsEnabled && {
            contentEditor: i18nTCommon(
              'settings.userRoles.contentEditor.label',
            ),
            projectApprover: i18nTCommon(
              'settings.userRoles.projectApprover.label',
              {
                interpolation: { escapeValue: false },
                singularRAMSName: voca.capitalize(singularRAMSName),
              },
            ),
            projectEditor: i18nTCommon(
              'settings.userRoles.projectEditor.label',
              {
                interpolation: { escapeValue: false },
                singularRAMSName: voca.capitalize(singularRAMSName),
              },
            ),
            projectViewer: i18nTCommon(
              'settings.userRoles.projectViewer.label',
              {
                interpolation: { escapeValue: false },
                singularRAMSName: voca.capitalize(singularRAMSName),
              },
            ),
          }),
        },
        tooltips: {
          administrator:
            areProjectsEnabled ?
              i18nTCommon('settings.userRoles.divisionAdmin.tooltip', {
                interpolation: { escapeValue: false },
                pluralRAMSName: voca.capitalize(pluralRAMSName),
              })
            : i18nTCommon(
                'settings.userRoles.divisionAdmin.tooltipWithoutProjects',
              ),
          personnelEditor: i18nTCommon(
            'settings.userRoles.personnelEditor.tooltip',
          ),
          personnelReporteeManager: i18nTCommon(
            'settings.userRoles.personnelReporteeManager.tooltip',
          ),
          personnelViewer: i18nTCommon(
            'settings.userRoles.personnelViewer.tooltip',
          ),
          rolesAndCoursesEditor:
            isCourseApplicableResourceForAtLeastOneDivisionWithinAccount ?
              i18nTCommon('settings.userRoles.rolesAndCoursesEditor.tooltip')
            : i18nTCommon('settings.userRoles.rolesEditor.tooltip'),
          ...(areProjectsEnabled && {
            contentEditor: i18nTCommon(
              'settings.userRoles.contentEditor.tooltip',
            ),
            projectApprover: i18nTCommon(
              'settings.userRoles.projectApprover.tooltip',
              {
                interpolation: { escapeValue: false },
                pluralRAMSName: voca.capitalize(pluralRAMSName),
                singularRAMSName: voca.capitalize(singularRAMSName),
              },
            ),
            projectEditor: i18nTCommon(
              'settings.userRoles.projectEditor.tooltip',
              {
                interpolation: { escapeValue: false },
                pluralRAMSName: voca.capitalize(pluralRAMSName),
                singularRAMSName: voca.capitalize(singularRAMSName),
              },
            ),
            projectViewer: i18nTCommon(
              'settings.userRoles.projectViewer.tooltip',
              {
                interpolation: { escapeValue: false },
                pluralRAMSName: voca.capitalize(pluralRAMSName),
                singularRAMSName: voca.capitalize(singularRAMSName),
              },
            ),
          }),
        },
      },
    }),
    [
      areProjectsEnabled,
      i18nTCommon,
      isCourseApplicableResourceForAtLeastOneDivisionWithinAccount,
      pluralRAMSName,
      singularRAMSName,
    ],
  );

  const columns = useMemo(
    () =>
      buildColumns({
        areProjectsEnabled,
        assignablePermissions,
        personnelText,
        pluralRAMSName,
        translations: columnTranslations,
      }),
    [
      areProjectsEnabled,
      assignablePermissions,
      columnTranslations,
      personnelText,
      pluralRAMSName,
    ],
  );

  const table = useReactTable({
    columns,
    data: divisions,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      currentUser,
      isFullUserAllowanceConsumptionDisabled,
      isPersonnelReporteeManagerAllowanceConsumptionDisabled,
      doesDomainUserHavePersonnelReporteeManagerForDivision,
      onPermissionChange,
      translations: {
        tooltips: {
          primaryDivision: i18nTCommon('shared.primaryDivision'),
          divisionAdministrators: i18nT('tooltips.divisionAdministrators'),
          higherPersonnelPermissionsEnabled: i18nTCommon(
            'settings.allUsersWithPermissionsToEditPersonnelCanViewThem',
          ),
          higherProjectPermissionsEnabled: i18nTCommon(
            'settings.allUsersWithHigherProjectPermissionsCanViewProjects',
            {
              interpolation: { escapeValue: false },
              pluralRAMSName,
              singularRAMSName,
            },
          ),
          notEnabled: {
            approvals: i18nT('tooltips.notEnabled.approvals'),
            contentEditing: i18nT('tooltips.notEnabled.contentEditing'),
            projects: i18nT('tooltips.notEnabled.projects', {
              interpolation: { escapeValue: false },
              pluralRAMSName: voca.capitalize(pluralRAMSName),
            }),
            teamsAndLineManagement: i18nT(
              'tooltips.notEnabled.teamsAndLineManagement',
            ),
          },
          rolesAndCoursesEditor: {
            courseApplicable: i18nT(
              'tooltips.rolesAndCoursesEditor.courseApplicable',
            ),
            default: i18nT('tooltips.rolesAndCoursesEditor.default'),
          },
        },
      },
    },
  });

  return (
    <>
      {isAtAnyAllowanceCapacity && (
        <AllowanceBar
          areProjectsEnabled={areProjectsEnabled}
          fullUserAllowanceConsumptionCount={fullUserAllowanceConsumptionCount}
          fullUserAllowanceCount={fullUserAllowanceCount}
          isAtFullUserCapacity={isAtFullUserCapacity}
          isAtPersonnelReporteeManagerCapacity={
            isAtPersonnelReporteeManagerCapacity
          }
          isPersonnelReporteeManagerEnabled={isPersonnelReporteeManagerEnabled}
          personnelReporteeManagerAllowanceConsumptionCount={
            personnelReporteeManagerAllowanceConsumptionCount
          }
          personnelReporteeManagerAllowanceCount={
            personnelReporteeManagerAllowanceCount
          }
        />
      )}
      <table className='tw-mt-10 tw-w-full'>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) =>
                header.isPlaceholder ?
                  <th colSpan={header.colSpan} key={header.id} />
                : (() => {
                    const { header: columnHeader, id: columnId } =
                      header.column.columnDef;

                    const isGroupHeader = header.subHeaders.length > 0;

                    return (
                      <th
                        className={classNames(
                          'tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-grey-050 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900',
                          columnId === 'division' ? 'tw-text-left' : (
                            'tw-text-center'
                          ),
                          isGroupHeader ? 'tw-h-8' : (
                            'tw-sticky -tw-top-px tw-z-[100] tw-h-12 tw-px-3 tw-py-2'
                          ),
                          {
                            'tw-border-l-3':
                              isGroupHeader ||
                              isFirstColumnInGroup(header.column),
                            'tw-w-25': columnId !== 'division',
                          },
                        )}
                        colSpan={header.colSpan}
                        key={header.id}
                        scope={isGroupHeader ? 'colgroup' : undefined}
                      >
                        {flexRender(columnHeader, header.getContext())}
                      </th>
                    );
                  })(),
              )}
            </tr>
          ))}
          <SelectAllRow
            accessControls={currentUser.accessControls}
            columns={table.getVisibleLeafColumns()}
            divisions={divisions}
            doesDomainUserHavePersonnelReporteeManagerForDivision={
              doesDomainUserHavePersonnelReporteeManagerForDivision
            }
            isFullUserAllowanceConsumptionDisabled={
              isFullUserAllowanceConsumptionDisabled
            }
            isPersonnelReporteeManagerAllowanceConsumptionDisabled={
              isPersonnelReporteeManagerAllowanceConsumptionDisabled
            }
            label={i18nT('selectAllRowLabel')}
            onSelectAllChange={onSelectAllChange}
            permissionLabels={columnTranslations.permissions.labels}
            tooltips={table.options.meta.translations.tooltips}
          />
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <AccessControlRow
              currentUser={currentUser}
              doesDomainUserHavePersonnelReporteeManager={doesDomainUserHavePersonnelReporteeManagerForDivision(
                row.original.id,
              )}
              isFullUserAllowanceConsumptionDisabled={
                isFullUserAllowanceConsumptionDisabled
              }
              isPersonnelReporteeManagerAllowanceConsumptionDisabled={
                isPersonnelReporteeManagerAllowanceConsumptionDisabled
              }
              key={row.id}
              row={row}
            />
          ))}
        </tbody>
      </table>
    </>
  );
}

AccessControlTable.propTypes = {
  fullUserAllowanceCount: PropTypes.number,
  assignablePermissions: PropTypes.array.isRequired,
  fullUserAllowanceConsumptionCount: PropTypes.number,
  personnelReporteeManagerAllowanceConsumptionCount: PropTypes.number,
  personnelReporteeManagerAllowanceCount: PropTypes.number,
  currentUser: PropTypes.object.isRequired,
  isAtFullUserCapacity: PropTypes.bool,
  isAtPersonnelReporteeManagerCapacity: PropTypes.bool,
  isFullUserAllowanceConsumptionDisabled: PropTypes.bool,
  isPersonnelReporteeManagerAllowanceConsumptionDisabled: PropTypes.bool,
  onPermissionChange: PropTypes.func.isRequired,
  onSelectAllChange: PropTypes.func.isRequired,
};

function SelectAllRow({
  accessControls,
  columns,
  divisions,
  doesDomainUserHavePersonnelReporteeManagerForDivision,
  isFullUserAllowanceConsumptionDisabled,
  isPersonnelReporteeManagerAllowanceConsumptionDisabled,
  label,
  onSelectAllChange,
  permissionLabels,
  tooltips,
}) {
  const doesDomainUserHavePersonnelReporteeManagerForAnyDivision = divisions
    .filter(({ meta }) =>
      meta.assignablePermissions.includes('personnelReporteeManager'),
    )
    .some(({ id }) =>
      doesDomainUserHavePersonnelReporteeManagerForDivision(id),
    );

  return (
    <tr className='tw-h-12 tw-bg-grey-025'>
      {columns.map((column) => {
        const isAllowanceExhaustedForDivision = (divisionId) =>
          isAllowanceExhaustedForColumn({
            columnId: column.id,
            doesDomainUserHavePersonnelReporteeManager:
              doesDomainUserHavePersonnelReporteeManagerForDivision(divisionId),
            isFullUserAllowanceConsumptionDisabled,
            isPersonnelReporteeManagerAllowanceConsumptionDisabled,
          });

        const isColumnAllowanceExhausted = isAllowanceExhaustedForColumn({
          columnId: column.id,
          doesDomainUserHavePersonnelReporteeManager:
            doesDomainUserHavePersonnelReporteeManagerForAnyDivision,
          isFullUserAllowanceConsumptionDisabled,
          isPersonnelReporteeManagerAllowanceConsumptionDisabled,
        });

        return (
          <td
            className={classNames(
              'tw-border-1 tw-border-solid tw-border-grey-100',
              {
                'tw-bg-grey-050': isColumnAllowanceExhausted,
                'tw-border-l-3': isFirstColumnInGroup(column),
              },
            )}
            key={column.id}
          >
            {column.id === 'division' ?
              <div className='tw-flex tw-px-3 tw-font-medium tw-text-grey-900'>
                {label}
              </div>
            : <SelectAllPermissionCell
                accessControls={accessControls}
                column={column}
                divisions={divisions}
                isAllowanceExhaustedForDivision={
                  isAllowanceExhaustedForDivision
                }
                onSelectAllChange={onSelectAllChange}
                permissionName={permissionLabels[column.id]}
                tooltips={tooltips}
              />
            }
          </td>
        );
      })}
    </tr>
  );
}

SelectAllRow.propTypes = {
  accessControls: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  divisions: PropTypes.array.isRequired,
  isFullUserAllowanceConsumptionDisabled: PropTypes.bool,
  isPersonnelReporteeManagerAllowanceConsumptionDisabled: PropTypes.bool,
  doesDomainUserHavePersonnelReporteeManagerForDivision:
    PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  onSelectAllChange: PropTypes.func.isRequired,
  permissionLabels: PropTypes.object.isRequired,
  tooltips: PropTypes.object.isRequired,
};

function SelectAllPermissionCell({
  accessControls,
  column,
  divisions,
  isAllowanceExhaustedForDivision,
  onSelectAllChange,
  permissionName,
  tooltips,
}) {
  const permission = column.id;
  const { disabledReason } = column.columnDef.meta || {};

  const assignableDivisions = divisions
    .filter(({ meta }) => meta.assignablePermissions.includes(permission))
    .map((division) => ({
      blockedReason: disabledReason?.({
        accessSelection: accessControls[division.id],
        assignablePermissions: division.meta.assignablePermissions,
      }),
      id: division.id,
      isActivelySelected: isPermissionActivelySelected(
        accessControls[division.id]?.[permission],
      ),
      isAllowanceExhausted: isAllowanceExhaustedForDivision(division.id),
      isSelected: isPermissionSelected(
        accessControls[division.id]?.[permission],
      ),
    }));

  const checkedDivisions = assignableDivisions.filter(
    ({ isAllowanceExhausted, isSelected }) =>
      isSelected && !isAllowanceExhausted,
  );

  const activelySelectedDivisions = assignableDivisions.filter(
    ({ isActivelySelected, isAllowanceExhausted }) =>
      isActivelySelected && !isAllowanceExhausted,
  );

  const areAllDivisionsSelected =
    assignableDivisions.length > 0 &&
    checkedDivisions.length === assignableDivisions.length;

  const isChecked =
    areAllDivisionsSelected || activelySelectedDivisions.length > 0;

  const deselectableDivisions = assignableDivisions.filter(
    ({ blockedReason, isSelected }) => !blockedReason && isSelected,
  );

  const selectableDivisions = assignableDivisions.filter(
    ({ blockedReason, isAllowanceExhausted }) =>
      !blockedReason && !isAllowanceExhausted,
  );

  const isDeselecting = isChecked && deselectableDivisions.length > 0;

  const togglingDivisions =
    isDeselecting ? deselectableDivisions : selectableDivisions;

  const blockingReason =
    togglingDivisions.length > 0 ?
      undefined
    : assignableDivisions.find(({ blockedReason }) => blockedReason)
        ?.blockedReason;

  return (
    <PermissionCheckboxCell
      checkedIcon={areAllDivisionsSelected ? CheckIcon : MinusIcon}
      isChecked={isChecked}
      isDeselecting={isDeselecting}
      isDisabled={togglingDivisions.length === 0}
      isSelectAll={true}
      name={`selectAll-${permission}`}
      onChange={() => {
        onSelectAllChange({
          divisionIds: togglingDivisions.map(({ id }) => id),
          isSelected: !isDeselecting,
          permission,
        });
      }}
      permissionName={permissionName}
      tooltip={blockingReason ? tooltips[blockingReason] : undefined}
    />
  );
}

SelectAllPermissionCell.propTypes = {
  accessControls: PropTypes.object.isRequired,
  column: PropTypes.object.isRequired,
  divisions: PropTypes.array.isRequired,
  isAllowanceExhaustedForDivision: PropTypes.func.isRequired,
  onSelectAllChange: PropTypes.func.isRequired,
  permissionName: PropTypes.string.isRequired,
  tooltips: PropTypes.object.isRequired,
};

const AccessControlRow = memo(
  // eslint-disable-next-line prefer-arrow-callback
  function AccessControlRow({
    _currentUser,
    doesDomainUserHavePersonnelReporteeManager,
    isFullUserAllowanceConsumptionDisabled,
    isPersonnelReporteeManagerAllowanceConsumptionDisabled,
    row,
  }) {
    const { assignablePermissions } = row.original.meta;

    return (
      <tr className='tw-h-12'>
        {row.getVisibleCells().map((cell) => {
          const { cell: columnCell, id: columnId } = cell.column.columnDef;

          const isCellDisabled =
            isAllowanceExhaustedForColumn({
              columnId,
              doesDomainUserHavePersonnelReporteeManager,
              isFullUserAllowanceConsumptionDisabled,
              isPersonnelReporteeManagerAllowanceConsumptionDisabled,
            }) ||
            !['division'].concat(assignablePermissions).includes(columnId);

          return (
            <td
              className={classNames(
                'tw-border-1 tw-border-solid tw-border-grey-100',
                {
                  'tw-bg-grey-050': isCellDisabled,
                  'tw-border-l-3': isFirstColumnInGroup(cell.column),
                },
              )}
              key={cell.id}
            >
              {flexRender(columnCell, cell.getContext())}
            </td>
          );
        })}
      </tr>
    );
  },
  (prevProps, nextProps) => {
    const { id: divisionId } = nextProps.row.original;

    return (
      prevProps.row === nextProps.row &&
      prevProps.isFullUserAllowanceConsumptionDisabled ===
        nextProps.isFullUserAllowanceConsumptionDisabled &&
      prevProps.isPersonnelReporteeManagerAllowanceConsumptionDisabled ===
        nextProps.isPersonnelReporteeManagerAllowanceConsumptionDisabled &&
      prevProps.doesDomainUserHavePersonnelReporteeManager ===
        nextProps.doesDomainUserHavePersonnelReporteeManager &&
      prevProps.currentUser.accessControls?.[divisionId] ===
        nextProps.currentUser.accessControls?.[divisionId]
    );
  },
);

AccessControlRow.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  currentUser: PropTypes.object.isRequired,
  isFullUserAllowanceConsumptionDisabled: PropTypes.bool,
  isPersonnelReporteeManagerAllowanceConsumptionDisabled: PropTypes.bool,
  doesDomainUserHavePersonnelReporteeManager: PropTypes.bool,
  row: PropTypes.object.isRequired,
};

function buildColumns({
  areProjectsEnabled,
  assignablePermissions,
  personnelText,
  pluralRAMSName,
  translations,
}) {
  return [
    {
      id: 'division',
      header: translations.division,
      cell: ({ row, table }) => {
        const { name, primary: isPrimary } = row.original.attributes;

        return (
          <div className='tw-flex tw-px-3 tw-font-medium tw-text-grey-900'>
            {isPrimary ?
              <Tooltip
                placement='top'
                tooltip={
                  table.options.meta.translations.tooltips.primaryDivision
                }
                trigger='hover'
              >
                <div className='tw-flex tw-items-center tw-gap-2'>
                  <HomeIcon
                    className='[&_path]:tw-fill-grey-900'
                    height={12}
                    width={14}
                  />
                  {name}
                </div>
              </Tooltip>
            : name}
          </div>
        );
      },
    },
    {
      id: 'administrator',
      header: () => (
        <PermissionHeader
          name={translations.permissions.labels.administrator}
          tooltip={translations.permissions.tooltips.administrator}
        />
      ),
      cell: ({ row, table }) => {
        const {
          attributes: { name: divisionName },
          id: divisionId,
        } = row.original;

        const {
          currentUser,
          isFullUserAllowanceConsumptionDisabled,
          onPermissionChange,
        } = table.options.meta;

        const currentAccessControl = currentUser.accessControls?.[divisionId];

        return (
          <PermissionCheckboxCell
            divisionName={divisionName}
            isChecked={
              !!currentAccessControl &&
              isPermissionSelected(currentAccessControl.administrator)
            }
            isDisabled={isFullUserAllowanceConsumptionDisabled}
            name='administrator'
            onChange={() => {
              onPermissionChange({ divisionId, permission: 'administrator' });
            }}
            permissionName={translations.permissions.labels.administrator}
          />
        );
      },
    },
    areProjectsEnabled && {
      id: 'projects',
      columns: [
        {
          id: 'projectViewer',
          meta: { disabledReason: projectViewerDisabledReason },
          header: () => (
            <PermissionHeader
              isFree={true}
              name={translations.permissions.labels.projectViewer}
              tooltip={translations.permissions.tooltips.projectViewer}
            />
          ),
          cell: ({ row, table }) => {
            const { assignablePermissions } = row.original.meta;

            const { projects: projectsNotEnabledTooltip } =
              table.options.meta.translations.tooltips.notEnabled;

            const {
              attributes: { name: divisionName },
              id: divisionId,
            } = row.original;

            const permissionName =
              translations.permissions.labels.projectViewer;

            if (!assignablePermissions.includes('projectViewer')) {
              return (
                <NonAssignablePermissionCell
                  divisionName={divisionName}
                  permission='projectViewer'
                  permissionName={permissionName}
                  tooltip={projectsNotEnabledTooltip}
                />
              );
            }

            const {
              currentUser,
              onPermissionChange,
              translations: { tooltips },
            } = table.options.meta;

            const accessSelection = currentUser.accessControls?.[divisionId];

            const blockedReason = projectViewerDisabledReason({
              accessSelection,
              assignablePermissions,
            });

            return (
              <PermissionCheckboxCell
                divisionName={divisionName}
                isChecked={isPermissionSelected(accessSelection?.projectViewer)}
                isDisabled={!!blockedReason}
                name='projectViewer'
                onChange={() => {
                  onPermissionChange({
                    divisionId,
                    permission: 'projectViewer',
                  });
                }}
                permissionName={permissionName}
                tooltip={blockedReason ? tooltips[blockedReason] : undefined}
              />
            );
          },
        },
        {
          id: 'projectEditor',
          meta: { disabledReason: administratorDisabledReason },
          header: () => (
            <PermissionHeader
              name={translations.permissions.labels.projectEditor}
              tooltip={translations.permissions.tooltips.projectEditor}
            />
          ),
          cell: ({ row, table }) => {
            const { assignablePermissions } = row.original.meta;

            const { projects: projectsNotEnabledTooltip } =
              table.options.meta.translations.tooltips.notEnabled;

            const {
              attributes: { name: divisionName },
              id: divisionId,
            } = row.original;

            const permissionName =
              translations.permissions.labels.projectEditor;

            if (!assignablePermissions.includes('projectEditor')) {
              return (
                <NonAssignablePermissionCell
                  divisionName={divisionName}
                  permission='projectEditor'
                  permissionName={permissionName}
                  tooltip={projectsNotEnabledTooltip}
                />
              );
            }

            const {
              currentUser,
              isFullUserAllowanceConsumptionDisabled,
              onPermissionChange,
              translations: { tooltips },
            } = table.options.meta;

            const accessSelection = currentUser.accessControls?.[divisionId];

            const blockedReason = administratorDisabledReason({
              accessSelection,
            });

            return (
              <PermissionCheckboxCell
                divisionName={divisionName}
                isChecked={isPermissionSelected(accessSelection?.projectEditor)}
                isDisabled={
                  !!blockedReason || isFullUserAllowanceConsumptionDisabled
                }
                name='projectEditor'
                onChange={() => {
                  onPermissionChange({
                    divisionId,
                    permission: 'projectEditor',
                  });
                }}
                permissionName={permissionName}
                tooltip={blockedReason ? tooltips[blockedReason] : undefined}
              />
            );
          },
        },
        assignablePermissions.includes('projectApprover') && {
          id: 'projectApprover',
          meta: { disabledReason: administratorDisabledReason },
          header: () => (
            <PermissionHeader
              name={translations.permissions.labels.projectApprover}
              tooltip={translations.permissions.tooltips.projectApprover}
            />
          ),
          cell: ({ row, table }) => {
            const { assignablePermissions } = row.original.meta;

            const { approvals: approvalsNotEnabledTooltip } =
              table.options.meta.translations.tooltips.notEnabled;

            const {
              attributes: { name: divisionName },
              id: divisionId,
            } = row.original;

            const permissionName =
              translations.permissions.labels.projectApprover;

            if (!assignablePermissions.includes('projectApprover')) {
              return (
                <NonAssignablePermissionCell
                  divisionName={divisionName}
                  permission='projectApprover'
                  permissionName={permissionName}
                  tooltip={approvalsNotEnabledTooltip}
                />
              );
            }

            const {
              currentUser,
              isFullUserAllowanceConsumptionDisabled,
              onPermissionChange,
              translations: { tooltips },
            } = table.options.meta;

            const accessSelection = currentUser.accessControls?.[divisionId];

            const blockedReason = administratorDisabledReason({
              accessSelection,
            });

            return (
              <PermissionCheckboxCell
                divisionName={divisionName}
                isChecked={isPermissionSelected(
                  accessSelection?.projectApprover,
                )}
                isDisabled={
                  !!blockedReason || isFullUserAllowanceConsumptionDisabled
                }
                name='projectApprover'
                onChange={() => {
                  onPermissionChange({
                    divisionId,
                    permission: 'projectApprover',
                  });
                }}
                permissionName={permissionName}
                tooltip={blockedReason ? tooltips[blockedReason] : undefined}
              />
            );
          },
        },
        assignablePermissions.includes('contentEditor') && {
          id: 'contentEditor',
          meta: { disabledReason: administratorDisabledReason },
          header: () => (
            <PermissionHeader
              name={translations.permissions.labels.contentEditor}
              tooltip={translations.permissions.tooltips.contentEditor}
            />
          ),
          cell: ({ row, table }) => {
            const { assignablePermissions } = row.original.meta;

            const { contentEditing: contentEditingNotEnabledTooltip } =
              table.options.meta.translations.tooltips.notEnabled;

            const {
              attributes: { name: divisionName },
              id: divisionId,
            } = row.original;

            const permissionName =
              translations.permissions.labels.contentEditor;

            if (!assignablePermissions.includes('contentEditor')) {
              return (
                <NonAssignablePermissionCell
                  divisionName={divisionName}
                  permission='contentEditor'
                  permissionName={permissionName}
                  tooltip={contentEditingNotEnabledTooltip}
                />
              );
            }

            const {
              currentUser,
              isFullUserAllowanceConsumptionDisabled,
              onPermissionChange,
              translations: { tooltips },
            } = table.options.meta;

            const accessSelection = currentUser.accessControls?.[divisionId];

            const blockedReason = administratorDisabledReason({
              accessSelection,
            });

            return (
              <PermissionCheckboxCell
                divisionName={divisionName}
                isChecked={isPermissionSelected(accessSelection?.contentEditor)}
                isDisabled={
                  !!blockedReason || isFullUserAllowanceConsumptionDisabled
                }
                name='contentEditor'
                onChange={() => {
                  onPermissionChange({
                    divisionId,
                    permission: 'contentEditor',
                  });
                }}
                permissionName={permissionName}
                tooltip={blockedReason ? tooltips[blockedReason] : undefined}
              />
            );
          },
        },
      ].filter(Boolean),
      header: () => voca.capitalize(pluralRAMSName),
    },
    {
      id: 'personnel',
      columns: [
        {
          id: 'personnelViewer',
          meta: { disabledReason: personnelViewerDisabledReason },
          header: () => (
            <PermissionHeader
              name={translations.permissions.labels.personnelViewer}
              tooltip={translations.permissions.tooltips.personnelViewer}
            />
          ),
          cell: ({ row, table }) => {
            const {
              attributes: { name: divisionName },
              id: divisionId,
            } = row.original;

            const {
              currentUser,
              isFullUserAllowanceConsumptionDisabled,
              onPermissionChange,
              translations: { tooltips },
            } = table.options.meta;

            const accessSelection = currentUser.accessControls?.[divisionId];

            const blockedReason = personnelViewerDisabledReason({
              accessSelection,
            });

            return (
              <PermissionCheckboxCell
                divisionName={divisionName}
                isChecked={isPermissionSelected(
                  accessSelection?.personnelViewer,
                )}
                isDisabled={
                  !!blockedReason || isFullUserAllowanceConsumptionDisabled
                }
                name='personnelViewer'
                onChange={() => {
                  onPermissionChange({
                    divisionId,
                    permission: 'personnelViewer',
                  });
                }}
                permissionName={translations.permissions.labels.personnelViewer}
                tooltip={blockedReason ? tooltips[blockedReason] : undefined}
              />
            );
          },
        },
        {
          id: 'personnelEditor',
          meta: { disabledReason: administratorDisabledReason },
          header: () => (
            <PermissionHeader
              name={translations.permissions.labels.personnelEditor}
              tooltip={translations.permissions.tooltips.personnelEditor}
            />
          ),
          cell: ({ row, table }) => {
            const {
              attributes: { name: divisionName },
              id: divisionId,
            } = row.original;

            const {
              currentUser,
              isFullUserAllowanceConsumptionDisabled,
              onPermissionChange,
              translations: { tooltips },
            } = table.options.meta;

            const accessSelection = currentUser.accessControls?.[divisionId];

            const blockedReason = administratorDisabledReason({
              accessSelection,
            });

            return (
              <PermissionCheckboxCell
                divisionName={divisionName}
                isChecked={isPermissionSelected(
                  accessSelection?.personnelEditor,
                )}
                isDisabled={
                  !!blockedReason || isFullUserAllowanceConsumptionDisabled
                }
                name='personnelEditor'
                onChange={() => {
                  onPermissionChange({
                    divisionId,
                    permission: 'personnelEditor',
                  });
                }}
                permissionName={translations.permissions.labels.personnelEditor}
                tooltip={blockedReason ? tooltips[blockedReason] : undefined}
              />
            );
          },
        },
        {
          id: 'rolesAndCoursesEditor',
          header: () => (
            <PermissionHeader
              name={translations.permissions.labels.rolesAndCoursesEditor}
              tooltip={translations.permissions.tooltips.rolesAndCoursesEditor}
            />
          ),
          cell: ({ row, table }) => {
            const {
              attributes: { name: divisionName },
              id: divisionId,
              meta: { isCourseApplicableResource },
            } = row.original;

            const {
              currentUser,
              isFullUserAllowanceConsumptionDisabled,
              onPermissionChange,
              translations: {
                tooltips: {
                  rolesAndCoursesEditor: {
                    courseApplicable: courseApplicableTooltip,
                    default: defaultTooltip,
                  },
                },
              },
            } = table.options.meta;

            const currentAccessControl =
              currentUser.accessControls?.[divisionId];

            return (
              <PermissionCheckboxCell
                divisionName={divisionName}
                isChecked={
                  !!currentAccessControl &&
                  isPermissionSelected(
                    currentAccessControl.rolesAndCoursesEditor,
                  )
                }
                isDisabled={isFullUserAllowanceConsumptionDisabled}
                name='rolesAndCoursesEditor'
                onChange={() => {
                  onPermissionChange({
                    divisionId,
                    permission: 'rolesAndCoursesEditor',
                  });
                }}
                permissionName={
                  translations.permissions.labels.rolesAndCoursesEditor
                }
                tooltip={
                  isCourseApplicableResource ?
                    courseApplicableTooltip
                  : defaultTooltip
                }
              />
            );
          },
        },
        assignablePermissions.includes('personnelReporteeManager') && {
          id: 'personnelReporteeManager',
          meta: { disabledReason: administratorDisabledReason },
          header: () => (
            <PermissionHeader
              name={translations.permissions.labels.personnelReporteeManager}
              tooltip={
                translations.permissions.tooltips.personnelReporteeManager
              }
            />
          ),
          cell: ({ row, table }) => {
            const { assignablePermissions } = row.original.meta;

            const {
              teamsAndLineManagement: teamsAndLineManagementNotEnabledTooltip,
            } = table.options.meta.translations.tooltips.notEnabled;

            const {
              attributes: { name: divisionName },
              id: divisionId,
            } = row.original;

            const permissionName =
              translations.permissions.labels.personnelReporteeManager;

            if (!assignablePermissions.includes('personnelReporteeManager')) {
              return (
                <NonAssignablePermissionCell
                  divisionName={divisionName}
                  permission='personnelReporteeManager'
                  permissionName={permissionName}
                  tooltip={teamsAndLineManagementNotEnabledTooltip}
                />
              );
            }

            const {
              currentUser,
              doesDomainUserHavePersonnelReporteeManagerForDivision,
              isPersonnelReporteeManagerAllowanceConsumptionDisabled,
              onPermissionChange,
              translations: { tooltips },
            } = table.options.meta;

            const accessSelection = currentUser.accessControls?.[divisionId];

            const blockedReason = administratorDisabledReason({
              accessSelection,
            });

            const isAllowanceExhausted =
              isPersonnelReporteeManagerAllowanceConsumptionDisabled &&
              !doesDomainUserHavePersonnelReporteeManagerForDivision(
                divisionId,
              );

            return (
              <PermissionCheckboxCell
                divisionName={divisionName}
                isChecked={
                  !isAllowanceExhausted &&
                  isPermissionSelected(
                    accessSelection?.personnelReporteeManager,
                  )
                }
                isDisabled={!!blockedReason || isAllowanceExhausted}
                name='personnelReporteeManager'
                onChange={() => {
                  onPermissionChange({
                    divisionId,
                    permission: 'personnelReporteeManager',
                  });
                }}
                permissionName={permissionName}
                tooltip={blockedReason ? tooltips[blockedReason] : undefined}
              />
            );
          },
        },
      ].filter(Boolean),
      header: () => personnelText,
    },
  ].filter(Boolean);
}

function administratorDisabledReason({ accessSelection }) {
  return isPermissionActivelySelected(accessSelection?.administrator) ?
      'divisionAdministrators'
    : null;
}

function projectViewerDisabledReason({
  accessSelection,
  assignablePermissions,
}) {
  if (isPermissionActivelySelected(accessSelection?.administrator))
    return 'divisionAdministrators';

  return (
      isAnyPermissionActivelySelected({
        accessSelection,
        permissionsToCheck: ['projectEditor'].concat(
          _.intersection(
            ['contentEditor', 'projectApprover'],
            assignablePermissions,
          ),
        ),
      })
    ) ?
      'higherProjectPermissionsEnabled'
    : null;
}

function personnelViewerDisabledReason({ accessSelection }) {
  if (isPermissionActivelySelected(accessSelection?.administrator))
    return 'divisionAdministrators';

  return isPermissionActivelySelected(accessSelection?.personnelEditor) ?
      'higherPersonnelPermissionsEnabled'
    : null;
}

function PermissionHeader({ isFree = false, name, tooltip }) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <>
      {isFree && (
        <div className='tw-absolute tw-right-0 tw-top-0 tw-z-[-1] tw-rounded-sm tw-bg-lime-050 tw-px-2 tw-py-1 tw-text-xs tw-font-bold tw-text-lime-700'>
          {i18nTShared('free')}
        </div>
      )}
      {name}&nbsp;
      <div className='tw-inline-block'>
        <Tooltip
          className='tooltip-dark--max-w-xxs tw-whitespace-pre-line'
          placement='top'
          tooltip={tooltip}
          trigger='hover'
        >
          <CircleQuestion size='sm' />
        </Tooltip>
      </div>
    </>
  );
}

PermissionHeader.propTypes = {
  isFree: PropTypes.bool,
  name: PropTypes.string.isRequired,
  tooltip: PropTypes.string.isRequired,
};

function NonAssignablePermissionCell({
  divisionName,
  permission,
  permissionName,
  tooltip,
}) {
  return (
    <PermissionCheckboxCell
      divisionName={divisionName}
      isChecked={false}
      isDisabled={true}
      name={permission}
      onChange={() => {}}
      permissionName={permissionName}
      tooltip={tooltip}
    />
  );
}

NonAssignablePermissionCell.propTypes = {
  divisionName: PropTypes.string.isRequired,
  permission: PropTypes.string.isRequired,
  permissionName: PropTypes.string.isRequired,
  tooltip: PropTypes.string.isRequired,
};

function PermissionCheckboxCell({
  checkedIcon,
  divisionName,
  isChecked,
  isDeselecting = false,
  isDisabled = false,
  isSelectAll = false,
  name,
  onChange,
  permissionName,
  tooltip,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.userForm.components.AccessControlTable',
  });

  // The select all row spans every division, so it names its action rather than a
  // division. That is why divisionName is optional and isSelectAll is explict
  const label =
    isSelectAll ?
      i18nT(
        isDeselecting ?
          'selectAllCheckbox.deselectLabel'
        : 'selectAllCheckbox.selectLabel',
        { interpolation: { escapeValue: false }, permissionName },
      )
    : i18nT('permissionCheckbox.label', {
        divisionName,
        interpolation: { escapeValue: false },
        permissionName,
      });

  return (
    <div className='tw-m-auto tw-w-5.5'>
      <ConditionalWrapper
        condition={!!tooltip}
        wrapper={(children) => (
          <Tooltip
            delayShow={tooltipDelay}
            placement='top'
            tooltip={tooltip}
            trigger='hover'
          >
            {children}
          </Tooltip>
        )}
      >
        <Checkbox
          checkedIcon={checkedIcon}
          isChecked={isChecked}
          isDisabled={isDisabled}
          label={label}
          name={name}
          onChange={onChange}
          size='md'
        />
      </ConditionalWrapper>
    </div>
  );
}

PermissionCheckboxCell.propTypes = {
  checkedIcon: PropTypes.elementType,
  divisionName: PropTypes.string,
  isChecked: PropTypes.bool.isRequired,
  isDeselecting: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSelectAll: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  permissionName: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
};
